import { ProcessedSearchResult } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import { AnalyticsComponentName } from '@app/pages/i/analytics';
import Box from '@redbubble/design-system/src/Box';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
} from '@redbubble/next-analytics/gtm';
import { usePageProps } from '@redbubble/next-provider';
import {
  RSPageType,
  RSProductClickedLocation,
} from '@redbubble/next-provider/rudderstack';
import { AppProps } from 'next/app';
import { useMemo } from 'react';
import type { RecommendedProducts } from 'types/shop-bff/types';
import { CarouselSection } from '../../../../components/CarouselSection';
import {
  CarouselSectionContext,
  CarouselSectionContextValue,
} from '../../../../components/CarouselSection/carouselSectionContext';
import { SearchUUIDStorageKey } from '../../../../utils/searchUUIDStorage';
import {
  parseCarouselItemToAnalyticsEcommerceItem,
  trackGA4ViewItemListEvent,
} from '../../../i/analytics';
import type { SearchResultsPageProps } from '../../types';
import { ArtworkInfo } from '../ArtworkInfo';
import styles from './SimilarProductsSection.module.scss';
import {
  mapSimilarProductMediaCardProps,
  mapSimilarProductPriceProps,
} from './utils';

export interface SimilarProductsSectionProps {
  lastClickedItem: ProcessedSearchResult;
  recommendedProducts: NonNullable<RecommendedProducts>;
}
export const messages = defineMessages({
  sectionTitle: {
    defaultMessage: 'Suggested for you',
  },
});

export const SimilarProductsSection: React.FC<SimilarProductsSectionProps> = ({
  lastClickedItem,
  recommendedProducts,
}) => {
  const intl = useIntl();
  const { searchUUID, listsInclusions } = usePageProps<
    AppProps & SearchResultsPageProps
  >();

  const contextValue: CarouselSectionContextValue = useMemo(() => {
    return {
      mediaCardPropsMapper: mapSimilarProductMediaCardProps(listsInclusions),
      pricePropsMapper: mapSimilarProductPriceProps,
      analytics: {
        componentName: AnalyticsComponentName.SimilarProducts,
        listID: AnalyticsItemsList.SimilarProductsCarousel,
        searchUUIDStorageKey: SearchUUIDStorageKey.Latest,
        inViewTracking: () => {
          const analyticsItems = recommendedProducts
            .map((item, index) => {
              return parseCarouselItemToAnalyticsEcommerceItem({
                item,
                index,
              });
            })
            .filter(
              (
                item: AnalyticsEcommerceItem | null
              ): item is NonNullable<AnalyticsEcommerceItem> => !!item
            );

          trackGA4ViewItemListEvent({
            items: analyticsItems,
            listID: AnalyticsItemsList.SimilarProductsCarousel,
            topic: '',
            searchUUIDStorageKey: SearchUUIDStorageKey.Latest,
          });
        },
      },
      rsLocation: RSProductClickedLocation.SimilarProductsCarousel,
      rsPageType: RSPageType.Search,
    };
  }, [listsInclusions, recommendedProducts]);

  return (
    <Box className={styles.container} data-testid="similar-products-section">
      <CarouselSectionContext.Provider value={contextValue}>
        <CarouselSection.Container>
          {lastClickedItem.inventoryItem && (
            <ArtworkInfo
              inventoryItem={lastClickedItem.inventoryItem}
              sectionTitle={intl.formatMessage(messages.sectionTitle)}
            />
          )}
          <CarouselSection.Carousel>
            {recommendedProducts.map((product, index) => {
              return (
                <CarouselSection.CarouselSlide
                  key={`similar-products-carousel-slide-${product?.inventoryItem?.id}`}
                >
                  <CarouselSection.CarouselMediaCard
                    data={product}
                    isLast={index === recommendedProducts.length - 1}
                    index={index}
                    searchUUID={searchUUID}
                  />
                </CarouselSection.CarouselSlide>
              );
            })}
          </CarouselSection.Carousel>
        </CarouselSection.Container>
      </CarouselSectionContext.Provider>
    </Box>
  );
};
